import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { httpsCallable } from "firebase/functions";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { functions } from "../../firebase";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import {
  X,
  CheckCircle,
  WarningCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeSlash,
  Info,
  Plugs,
  User,
  Buildings,
  Gear,
  Check,
  Receipt,
  CurrencyCircleDollar,
  ShieldCheck,
  EnvelopeSimple,
  UsersThree,
} from "@phosphor-icons/react";
import {
  DI_DOCUMENT_TYPES,
  DI_PAYMENT_TYPES,
  DI_COMPANY_TYPES,
  getDocumentTypeOptions,
  getPaymentTypeOptions,
  getCompanyTypeOptions,
} from "../../services/digitalInvoiceService";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./DigitalInvoiceSetupWizard.css";
import LoaderLoop from "../shared/loader/LoaderLoop";

const WIZARD_STEPS = [
  { id: "welcome", title: "ברוכים הבאים", subtitle: "חשבונית דיגיטלית", icon: Plugs },
  { id: "account", title: "פרטי חשבון", subtitle: "יצירת משתמש", icon: User },
  { id: "company", title: "פרטי עסק", subtitle: "מידע על העסק", icon: Buildings },
  { id: "preferences", title: "העדפות", subtitle: "הגדרות הפקה", icon: Gear },
  { id: "complete", title: "סיום", subtitle: "הכל מוכן", icon: Check },
];

function DigitalInvoiceSetupWizard({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);

  const {
    checkDigitalInvoice,
    isChecking: isCheckingPlanPermission,
    PlanLimitModalComponent,
  } = usePlanPermissions();

  const [hasCheckedPermission, setHasCheckedPermission] = useState(false);
  const [isPermissionAllowed, setIsPermissionAllowed] = useState(true);

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Account data
  const [accountData, setAccountData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });

  // Company data
  const [companyData, setCompanyData] = useState({
    Name: "",
    EnglishName: "",
    UniqueID: "",
    Address: "",
    City: "",
    Phone: "",
    Email: "",
    CompanyType: DI_COMPANY_TYPES.AUTHORIZED_DEALER,
  });

  // Preferences
  const [preferences, setPreferences] = useState({
    defaultDocumentType: DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT,
    defaultPaymentType: DI_PAYMENT_TYPES.CASH,
    allowTherapistsToCreateInvoices: false,
  });

  const [showPassword, setShowPassword] = useState(false);

  // Check plan permission
  useEffect(() => {
    const checkPermission = async () => {
      if (isOpen && !hasCheckedPermission && checkDigitalInvoice) {
        setHasCheckedPermission(true);
        const allowed = await checkDigitalInvoice();
        setIsPermissionAllowed(allowed);
        if (!allowed) {
          setTimeout(() => onClose(), 100);
        }
      }
    };
    checkPermission();
  }, [isOpen, hasCheckedPermission, checkDigitalInvoice, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setHasCheckedPermission(false);
      setIsPermissionAllowed(true);
    }
  }, [isOpen]);

  const handleAccountChange = useCallback((field, value) => {
    setAccountData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  const handleCompanyChange = useCallback((field, value) => {
    setCompanyData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  const handlePreferencesChange = useCallback((field, value) => {
    setPreferences((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCreateAccount = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const createAccount = httpsCallable(functions, "createDigitalInvoiceAccount");
      const result = await createAccount({
        clinicId,
        accountData: {
          email: accountData.email,
          password: accountData.password,
          firstName: accountData.firstName,
          lastName: accountData.lastName,
        },
        companyData: {
          Name: companyData.Name,
          EnglishName: companyData.EnglishName,
          UniqueID: companyData.UniqueID,
          Address: companyData.Address,
          City: companyData.City,
          Phone: companyData.Phone,
          Email: companyData.Email,
          CompanyType: companyData.CompanyType,
        },
        preferences: {
          defaultDocumentType: preferences.defaultDocumentType,
          defaultPaymentType: preferences.defaultPaymentType,
          allowTherapistsToCreateInvoices: preferences.allowTherapistsToCreateInvoices,
        },
      });

      if (!result.data.success) {
        if (result.data.rawApiError) {
          console.error("[CK-9210] Digital Invoice API raw error:", {
            rawApiError: result.data.rawApiError,
            errorId: result.data.errorId || null,
            httpStatus: result.data.httpStatus || null,
            timestamp: new Date().toISOString(),
          });
        }
        setError(result.data.error || "שגיאה ביצירת החשבון - צור קשר עם התמיכה");
        return;
      }

      dispatch(
        setClinicProfile({
          ...clinicProfile,
          isDigitalInvoiceIntegrated: true,
          digitalInvoiceIntegration: result.data.digitalInvoiceIntegration,
        })
      );

      setCurrentStep(4);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("[CK-9210] Error creating Digital Invoice account:", {
        errorCode: "CK-9210",
        errorMessage: err.message,
        errorStack: err.stack,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה ביצירת החשבון - נסה שוב או צור קשר עם התמיכה");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 3) {
      handleCreateAccount();
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setError(null);
  };

  const handleClose = () => {
    setCurrentStep(0);
    setAccountData({ email: "", password: "", firstName: "", lastName: "" });
    setCompanyData({
      Name: "", EnglishName: "", UniqueID: "", Address: "", City: "", Phone: "", Email: "",
      CompanyType: DI_COMPANY_TYPES.AUTHORIZED_DEALER,
    });
    setPreferences({
      defaultDocumentType: DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT,
      defaultPaymentType: DI_PAYMENT_TYPES.CASH,
      allowTherapistsToCreateInvoices: false,
    });
    setError(null);
    setShowPassword(false);
    onClose();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return true;
      case 1:
        return (
          accountData.email.length >= 5 &&
          accountData.email.includes("@") &&
          accountData.password.length >= 8 &&
          accountData.firstName.trim().length > 0 &&
          accountData.lastName.trim().length > 0
        );
      case 2:
        return (
          companyData.Name.trim().length > 0 &&
          companyData.EnglishName.trim().length >= 2 &&
          companyData.UniqueID.trim().length > 0 &&
          companyData.Phone.trim().length >= 3 &&
          companyData.Email.trim().length > 0 &&
          companyData.Email.includes("@") &&
          companyData.Address.trim().length > 0 &&
          companyData.City.trim().length > 0
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  // ── Step Renderers ────────────────────────────

  const renderWelcomeStep = () => (
    <div className="di-wizard-step-content di-welcome-step">
      <div className="di-wizard-hero">
        <div className="di-hero-icon-wrapper">
          <Receipt size={48} weight="duotone" />
        </div>
        <h3>התחל להנפיק חשבוניות עם חשבונית דיגיטלית</h3>
        <p>צור חשבון והתחל להפיק חשבוניות מס וקבלות ישירות מ-CliniKit</p>
      </div>

      <div className="di-features-grid">
        <div className="di-feature-card">
          <div className="di-feature-icon">
            <Receipt size={24} weight="duotone" />
          </div>
          <div className="di-feature-content">
            <h4>הנפקה אוטומטית</h4>
            <p>חשבונית נוצרת אוטומטית לאחר קבלת תשלום</p>
          </div>
        </div>
        <div className="di-feature-card">
          <div className="di-feature-icon">
            <EnvelopeSimple size={24} weight="duotone" />
          </div>
          <div className="di-feature-content">
            <h4>שליחה במייל</h4>
            <p>שלח חשבוניות ישירות למטופל בלחיצה</p>
          </div>
        </div>
        <div className="di-feature-card">
          <div className="di-feature-icon">
            <CurrencyCircleDollar size={24} weight="duotone" />
          </div>
          <div className="di-feature-content">
            <h4>מעקב מרוכז</h4>
            <p>צפה בכל המסמכים במקום אחד</p>
          </div>
        </div>
        <div className="di-feature-card">
          <div className="di-feature-icon">
            <ShieldCheck size={24} weight="duotone" />
          </div>
          <div className="di-feature-content">
            <h4>עמידה בתקנות</h4>
            <p>התאמה מלאה לדרישות רשות המסים</p>
          </div>
        </div>
      </div>

      <div className="di-wizard-info-banner">
        <Info size={20} weight="fill" />
        <div>
          <strong>מה יקרה עכשיו?</strong>
          <p>ניצור עבורך חשבון בחשבונית דיגיטלית ותוכל להתחיל להפיק חשבוניות מיד.</p>
        </div>
      </div>
    </div>
  );

  const renderAccountStep = () => (
    <div className="di-wizard-step-content di-account-step">
      <div className="di-step-description">
        <h3>פרטי החשבון</h3>
        <p>ניצור עבורך חשבון בחשבונית דיגיטלית. מלא את הפרטים הבאים:</p>
      </div>

      <div className="di-form-grid">
        <div className="di-form-group">
          <label htmlFor="di-first-name">שם פרטי</label>
          <input
            id="di-first-name"
            type="text"
            value={accountData.firstName}
            onChange={(e) => handleAccountChange("firstName", e.target.value)}
            placeholder="שם פרטי"
            autoComplete="given-name"
          />
        </div>

        <div className="di-form-group">
          <label htmlFor="di-last-name">שם משפחה</label>
          <input
            id="di-last-name"
            type="text"
            value={accountData.lastName}
            onChange={(e) => handleAccountChange("lastName", e.target.value)}
            placeholder="שם משפחה"
            autoComplete="family-name"
          />
        </div>

        <div className="di-form-group di-full-width">
          <label htmlFor="di-email">אימייל (ישמש כשם משתמש)</label>
          <input
            id="di-email"
            type="email"
            value={accountData.email}
            onChange={(e) => handleAccountChange("email", e.target.value)}
            placeholder="email@example.com"
            dir="ltr"
            autoComplete="email"
          />
        </div>

        <div className="di-form-group di-full-width">
          <label htmlFor="di-password">סיסמה (לפחות 8 תווים)</label>
          <div className="di-password-input-wrapper">
            <input
              id="di-password"
              type={showPassword ? "text" : "password"}
              value={accountData.password}
              onChange={(e) => handleAccountChange("password", e.target.value)}
              placeholder="בחר סיסמה"
              dir="ltr"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="di-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {accountData.password.length > 0 && accountData.password.length < 8 && (
            <span className="di-field-hint di-hint-warning">
              <WarningCircle size={14} /> הסיסמה חייבת להכיל לפחות 8 תווים
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderCompanyStep = () => (
    <div className="di-wizard-step-content di-company-step">
      <div className="di-step-description">
        <h3>פרטי העסק</h3>
        <p>פרטים אלו ישמשו להפקת החשבוניות שלך</p>
      </div>

      <div className="di-form-grid">
        <div className="di-form-group di-full-width">
          <label htmlFor="di-company-name">שם העסק *</label>
          <input
            id="di-company-name"
            type="text"
            value={companyData.Name}
            onChange={(e) => handleCompanyChange("Name", e.target.value)}
            placeholder="שם העסק בעברית כפי שיופיע בחשבונית"
          />
        </div>

        <div className="di-form-group di-full-width">
          <label htmlFor="di-company-english-name">שם העסק באנגלית *</label>
          <input
            id="di-company-english-name"
            type="text"
            value={companyData.EnglishName}
            onChange={(e) => handleCompanyChange("EnglishName", e.target.value)}
            placeholder="Business Name (English)"
            dir="ltr"
          />
          {companyData.EnglishName.length > 0 && companyData.EnglishName.length < 2 && (
            <span className="di-field-hint di-hint-warning">
              <WarningCircle size={14} /> לפחות 2 תווים באנגלית
            </span>
          )}
        </div>

        <div className="di-form-group">
          <label htmlFor="di-unique-id">ח.פ. / ת.ז. *</label>
          <input
            id="di-unique-id"
            type="text"
            value={companyData.UniqueID}
            onChange={(e) => handleCompanyChange("UniqueID", e.target.value)}
            placeholder="מספר זהות ישראלי תקין"
            dir="ltr"
          />
        </div>

        <div className="di-form-group">
          <label htmlFor="di-company-type">סוג עוסק *</label>
          <select
            id="di-company-type"
            value={companyData.CompanyType}
            onChange={(e) => handleCompanyChange("CompanyType", Number(e.target.value))}
          >
            {getCompanyTypeOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="di-form-group">
          <label htmlFor="di-company-phone">טלפון *</label>
          <input
            id="di-company-phone"
            type="tel"
            value={companyData.Phone}
            onChange={(e) => handleCompanyChange("Phone", e.target.value)}
            placeholder="0500000000"
            dir="ltr"
          />
          <span className="di-field-hint">ללא קידומת מדינה, פורמט מקומי</span>
        </div>

        <div className="di-form-group">
          <label htmlFor="di-company-email">אימייל חברה *</label>
          <input
            id="di-company-email"
            type="email"
            value={companyData.Email}
            onChange={(e) => handleCompanyChange("Email", e.target.value)}
            placeholder="billing@company.com"
            dir="ltr"
          />
        </div>

        <div className="di-form-group">
          <label htmlFor="di-company-address">כתובת *</label>
          <input
            id="di-company-address"
            type="text"
            value={companyData.Address}
            onChange={(e) => handleCompanyChange("Address", e.target.value)}
            placeholder="כתובת העסק"
          />
        </div>

        <div className="di-form-group">
          <label htmlFor="di-company-city">עיר *</label>
          <input
            id="di-company-city"
            type="text"
            value={companyData.City}
            onChange={(e) => handleCompanyChange("City", e.target.value)}
            placeholder="עיר"
          />
        </div>
      </div>
    </div>
  );

  const renderPreferencesStep = () => (
    <div className="di-wizard-step-content di-preferences-step">
      <div className="di-step-description">
        <h3>העדפות הפקה</h3>
        <p>הגדר ברירות מחדל להפקת מסמכים</p>
      </div>

      <div className="di-form-grid">
        <div className="di-form-group di-full-width">
          <label htmlFor="di-default-doc-type">סוג מסמך ברירת מחדל</label>
          <select
            id="di-default-doc-type"
            value={preferences.defaultDocumentType}
            onChange={(e) => handlePreferencesChange("defaultDocumentType", Number(e.target.value))}
          >
            {getDocumentTypeOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="di-form-group di-full-width">
          <label htmlFor="di-default-pay-type">אמצעי תשלום ברירת מחדל</label>
          <select
            id="di-default-pay-type"
            value={preferences.defaultPaymentType}
            onChange={(e) => handlePreferencesChange("defaultPaymentType", Number(e.target.value))}
          >
            {getPaymentTypeOptions().map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="di-form-group di-full-width">
          <div className="di-toggle-row">
            <div className="di-toggle-info">
              <UsersThree size={20} weight="duotone" />
              <div>
                <strong>אפשר למטפלים להפיק חשבוניות</strong>
                <p>כל מטפלי הקליניקה יוכלו להפיק חשבוניות, לא רק מנהלים</p>
              </div>
            </div>
            <label className="di-toggle-switch">
              <input
                type="checkbox"
                checked={preferences.allowTherapistsToCreateInvoices}
                onChange={(e) =>
                  handlePreferencesChange("allowTherapistsToCreateInvoices", e.target.checked)
                }
              />
              <span className="di-toggle-slider" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="di-wizard-step-content di-complete-step">
      <div className="di-success-icon">
        <CheckCircle size={64} weight="duotone" />
      </div>
      <h3>החשבון נוצר בהצלחה!</h3>
      <p>חשבונית דיגיטלית מחוברת ומוכנה לשימוש</p>

      <div className="di-next-steps">
        <h4>מה עכשיו?</h4>
        <div className="di-next-steps-list">
          <div className="di-next-step-item">
            <span className="di-step-number">1</span>
            <span>הפק חשבונית מתוך עמוד ניהול חשבוניות</span>
          </div>
          <div className="di-next-step-item">
            <span className="di-step-number">2</span>
            <span>גרור כרטיס ב-Kanban לעמודת "הופקה חשבונית" להפקה אוטומטית</span>
          </div>
          <div className="di-next-step-item">
            <span className="di-step-number">3</span>
            <span>צפה בכל המסמכים בטאב "ניהול חשבוניות" בהגדרות</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderWelcomeStep();
      case 1: return renderAccountStep();
      case 2: return renderCompanyStep();
      case 3: return renderPreferencesStep();
      case 4: return renderCompleteStep();
      default: return null;
    }
  };

  const getNextButtonLabel = () => {
    switch (currentStep) {
      case 0: return "בוא נתחיל";
      case 3: return "צור חשבון והתחבר";
      default: return "המשך";
    }
  };

  if (!isOpen) return null;

  if (isCheckingPlanPermission || !isPermissionAllowed) {
    return <><PlanLimitModalComponent /></>;
  }

  return (
    <>
      <div className="di-wizard-overlay" onClick={handleClose}>
        <div className="di-wizard-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="di-wizard-header">
            <div className="di-wizard-header-content">
              <div className="di-header-icon-wrapper">
                <Receipt size={28} weight="duotone" />
              </div>
              <div className="di-wizard-title">
                <h2>{WIZARD_STEPS[Math.min(currentStep, WIZARD_STEPS.length - 1)].title}</h2>
                <span className="di-wizard-subtitle">
                  {WIZARD_STEPS[Math.min(currentStep, WIZARD_STEPS.length - 1)].subtitle}
                </span>
              </div>
            </div>
            <div className="di-wizard-header-actions">
              <ModalCloseButton onClick={handleClose} />
            </div>
          </div>

          {/* Progress */}
          {currentStep < 4 && (
            <div className="di-wizard-progress">
              {WIZARD_STEPS.slice(0, 4).map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <div
                    key={step.id}
                    className={`di-progress-step ${
                      index === currentStep ? "active" : ""
                    } ${index < currentStep ? "completed" : ""}`}
                  >
                    <div className="di-step-icon-circle">
                      {index < currentStep ? (
                        <Check size={14} weight="bold" />
                      ) : (
                        <StepIcon size={14} weight={index === currentStep ? "bold" : "regular"} />
                      )}
                    </div>
                    <span className="di-step-label">{step.title}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Content */}
          <div className="di-wizard-body">
            {renderStepContent()}

            {error && (
              <div className="di-wizard-error">
                <WarningCircle size={18} weight="fill" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="di-wizard-footer">
            {currentStep === 4 ? (
              <button className="di-wizard-btn di-btn-primary" onClick={handleClose}>
                סגור
              </button>
            ) : (
              <>
                {currentStep > 0 && (
                  <button
                    className="di-wizard-btn di-btn-secondary"
                    onClick={handleBack}
                    disabled={isLoading}
                  >
                    <ArrowRight size={16} />
                    <span>חזור</span>
                  </button>
                )}
                <button
                  className="di-wizard-btn di-btn-primary"
                  onClick={handleNext}
                  disabled={!canProceed() || isLoading}
                >
                  {isLoading ? (
                    <>
                      <LoaderLoop size="xs" />
                      <span>רגע...</span>
                    </>
                  ) : (
                    <>
                      <span>{getNextButtonLabel()}</span>
                      {currentStep < 3 && <ArrowLeft size={16} />}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <PlanLimitModalComponent />
    </>
  );
}

export default DigitalInvoiceSetupWizard;
